import React, { useState, useRef, useEffect } from 'react';
import { DropdownContainer, DropdownButton, DropdownList, DropdownLabel } from './index.styles';
import { ICurrencySelectorProps } from './index.types';
import { CustomModal } from '../CustomModal';
import { useScreenViewport, getCurrencySymbol, updateCurrencies, setSessionKey } from '../../utils';
import { constants } from '../../constants';
import { usePageContext } from '../../organisms';
import { Input } from '../../atoms';

export const CurrencySelector = ({
  selectedCurrency,
  propertyCurrency,
  onChangeHandler,
  ...props
}: ICurrencySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedCurrency);
  const { pageState } = usePageContext();
  const { rlmTabList, rateCard } = pageState;

  const dropdownRef = useRef<HTMLDivElement>(null);

  const isMobileView = useScreenViewport() === constants.MOBILE;

  const { modalTitle, dropdownLabel, currencies } = props;

  const updatedCurrencies = updateCurrencies(currencies, propertyCurrency, selectedCurrency);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    handleOptionSelect(event.currentTarget.value);
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const { screenX, screenY, currentTarget } = event || {};
    const value = currentTarget.value;

    handleOptionSelect(screenX === 0 && screenY === 0 ? '' : value);
  };

  const handleOptionSelect = (value: string) => {
    if (value) {
      setSelected(value);
      setSessionKey(constants.SELECTED_TAB, rlmTabList?.selectedTab);
      if (rateCard?.showRatesWithTaxes) {
        setSessionKey(constants.SHOW_TAXES_AND_FEES, constants.SHOW_TAXES_AND_FEES_CHECKED);
      } else {
        setSessionKey(constants.SHOW_TAXES_AND_FEES, constants.SHOW_TAXES_AND_FEES_UNCHECKED);
      }
      onChangeHandler(value);
      setIsOpen(false);
    }
  };

  const handleDesktopClickOutside = (event: MouseEvent) => {
    if (!isMobileView && !dropdownRef?.current?.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isMobileView) {
      document.addEventListener('click', handleDesktopClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleDesktopClickOutside);
    };
  }, [isMobileView]);

  const DropdownListComp = () => {
    return (
      <DropdownList>
        <DropdownLabel>{dropdownLabel}</DropdownLabel>
        {updatedCurrencies?.map((currency, index) => (
          <Input
            {...props}
            type="radio"
            id={`currencyCode_${index}`}
            testId={`currencyCode_${index}`}
            className="radio label"
            name="currencyCode"
            label={currency.label}
            checked={selected === currency.value}
            value={currency.value}
            onClick={handleClick}
            onKeyPress={handleKeyPress}
            inputClassName="custom_click_track"
          />
        ))}
      </DropdownList>
    );
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton data-testid="currency-selector-btn" onClick={toggleDropdown}>
        {selected ? `${getCurrencySymbol(selected)} ${selected}` : 'Default'}
        <span className={isOpen ? 'icon-dropdown-up' : 'icon-dropdown-down'} />
      </DropdownButton>
      {isMobileView ? (
        <CustomModal
          openModal={isOpen}
          heading={modalTitle}
          styledClassName={'currency-selector-header'}
          handleClose={() => {
            setIsOpen(false);
          }}
          optionsModal={false}
          backArrow={false}
        >
          <DropdownListComp />
        </CustomModal>
      ) : (
        isOpen && <DropdownListComp />
      )}
    </DropdownContainer>
  );
};
